"use client";
import React from "react";
import { Shield, FileText, Eye, Lock, Users, Mail } from "lucide-react";

const TermsPrivacySection = () => {
  const sections = [
    {
      id: "terms",
      title: "Terms and Conditions",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            By accessing this website, you agree to these Website Terms and
            Conditions and applicable local laws. If you disagree with any
            terms, please do not use this site. All website content is protected
            by copyright and trademark laws.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <p className="text-blue-800 text-sm font-medium">
              <strong>Important:</strong> These terms constitute a legally
              binding agreement between you and Vasta Infotech.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "privacy",
      title: "Privacy Policy",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Our Commitment to You
            </h4>
            <p className="text-slate-700 leading-relaxed">
              Vasta Infotech values your privacy and takes steps to protect
              personal information shared through our website. We use visitor
              information only for internal business purposes and never sell or
              share it with third parties except as outlined below.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Information We Collect
            </h4>
            <p className="text-slate-700 leading-relaxed">
              We collect basic contact details like your name, email address,
              company name, and phone number only when voluntarily provided —
              such as when requesting information, subscribing to updates, or
              contacting us directly. No postal addresses are collected.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              How We Use Your Information
            </h4>
            <p className="text-slate-700 leading-relaxed">
              Any details you provide help us respond to your requests, deliver
              requested services, and improve our offerings. That's it.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Sharing Your Information
            </h4>
            <p className="text-slate-700 leading-relaxed">
              We don't sell, trade, or transfer your information to outside
              parties without consent, except when legally required. If Vasta
              Infotech merges, gets acquired, or restructures, your information
              may transfer to the new entity with appropriate user notification.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Access and Control
            </h4>
            <p className="text-slate-700 leading-relaxed" id="cookies">
              Want to update, correct, or remove your information? Email us at{" "}
              <a
                href="mailto:support@vastainfo.com"
                className="text-blue-600 hover:text-blue-800 font-medium underline decoration-blue-300 hover:decoration-blue-500 transition-colors"
              >
                support@vastainfo.com
              </a>{" "}
              or use our support page. We'll handle requests quickly per data
              protection laws.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3">
              Cookies
            </h4>
            <p className="text-slate-700 leading-relaxed">
              Our site uses cookies to improve functionality, track usage, and
              analyze visitor behavior. Disable them in browser settings if
              preferred (some features may not work).
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-3">
              Changes to This Policy
            </h4>
            <p className="text-slate-700 leading-relaxed">
              We may update this policy. Changes appear here with a new
              effective date. Check back periodically.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section
      className="relative py-12 lg:py-16 md:py-14 sm:py-10 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 overflow-hidden"
      aria-labelledby="terms-privacy-heading"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/3 left-1/5 w-64 h-64 lg:w-72 lg:h-72 md:w-56 md:h-56 sm:w-32 sm:h-32 bg-blue-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/5 w-64 h-64 lg:w-72 lg:h-72 md:w-56 md:h-56 sm:w-32 sm:h-32 bg-indigo-100/30 rounded-full blur-3xl"></div>

        {/* Floating icons */}
        <div className="absolute top-20 right-10 opacity-10">
          <Shield className="w-16 h-16 text-blue-600 animate-pulse" />
        </div>
        <div className="absolute bottom-20 left-10 opacity-10">
          <FileText className="w-12 h-12 text-indigo-600 animate-bounce" />
        </div>
      </div>

      <div className="container max-w-6xl mx-auto px-4 lg:px-6 md:px-4 sm:px-3 relative">
        {/* Header */}
        <header className="text-center mb-12 lg:mb-16 md:mb-14 sm:mb-10">
          <div className="inline-flex items-center px-4 py-2 lg:px-6 lg:py-2 md:px-4 md:py-1.5 sm:px-3 sm:py-1 bg-[#1719A8]/90 backdrop-blur-md border border-[#1719A8]/60 rounded-full mb-4 shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full mr-3 animate-pulse"></span>
            <span className="text-xs md:text-sm lg:text-sm sm:text-xs text-white font-semibold uppercase tracking-wider">
              Legal Information
            </span>
          </div>

          <h1
            id="terms-privacy-heading"
            className="text-3xl lg:text-5xl md:text-4xl sm:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 md:mb-5 sm:mb-3 leading-tight"
          >
            TERMS & PRIVACY{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              POLICY
            </span>
          </h1>

          <p className="text-lg lg:text-xl md:text-lg sm:text-sm text-slate-600 max-w-3xl mx-auto lg:max-w-4xl md:max-w-3xl sm:max-w-xl leading-relaxed">
            Your privacy and trust matter to us. Learn about our commitment to
            protecting your information and the terms that govern our services.
          </p>
        </header>

        {/* Content Cards */}
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section) => (
            <article
              key={section.id}
              id={section.id}
              className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 scroll-mt-24"
            >
              {/* Section Header */}
              <div className="p-6 lg:p-8 md:p-6 sm:p-4 border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                    {section.icon}
                  </div>
                  <div>
                    <h2 className="text-xl lg:text-2xl md:text-xl sm:text-lg font-bold text-slate-900">
                      {section.title}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Important information about our{" "}
                      {section.title.toLowerCase()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Content */}
              <div className="p-6 lg:p-8 md:p-6 sm:p-4">{section.content}</div>
            </article>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 lg:mt-16 md:mt-14 sm:mt-10 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 lg:p-10 md:p-8 sm:p-6 text-white shadow-xl">
            <h3 className="text-2xl lg:text-3xl md:text-2xl sm:text-xl font-bold mb-4">
              Questions About Our Policies?
            </h3>
            <p className="text-blue-100 mb-6 lg:text-lg md:text-base sm:text-sm max-w-2xl mx-auto">
              If you have any questions about our Terms and Conditions or
              Privacy Policy, we're here to help clarify anything you need to
              know.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@vastainfo.com"
                className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>

        {/* Last Updated Notice */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </section>
  );
};

export default TermsPrivacySection;
